// Shared program-source track attachment (LSC-561). This is the EXACT source-resolution
// logic extracted from ConsumerWatch so the fan watch AND the talent program monitors
// (GoLivePanel / GuestJoinScreen, via ProgramComposition) resolve a program slot to the
// same live track — a slot must never resolve to a different source on the fan vs the
// talent view (the LSC-177 property, now cross-surface). Pure (no React); every call site
// owns its own element + holder refs and calls these.
//
// Matching is by STABLE key (participantSourceKey, ADR 0042) and the live candidate is
// chosen by the SAME pickLiveCandidate rule the Director confidence monitors use (ADR
// 0075/0089/LSC-177). `force` re-attaches an unchanged track (element remount / hold lift,
// ADR 0062); otherwise attach is idempotent so an unrelated join/leave never flickers.

import { participantSourceKey, type SourceKind } from "./liveConnection";
import { pickLiveCandidate } from "./programLayout";

export interface AttachableTrack {
  attach(el: HTMLMediaElement): HTMLMediaElement;
  detach(el?: HTMLMediaElement): HTMLMediaElement | HTMLMediaElement[];
  setVolume?(volume: number): void;
}

// A remote track publication as we read it: the subscribed track (if any) plus the
// subscribe controls. Kept minimal so tests can supply a plain object.
export interface TrackPub {
  videoTrack?: AttachableTrack;
  audioTrack?: AttachableTrack;
  isSubscribed?: boolean;
  setSubscribed?: (s: boolean) => void;
}

/** A minimal room shape — just the remote participants we scan for the program source. */
export interface AttachRoom {
  remoteParticipants?: Map<string, unknown>;
}

export type TrackHolder = { current: AttachableTrack | null };

// Force a subscription on a publication that is on program but not yet subscribed — the
// late-joiner / second-publisher recovery (ADR 0075). Idempotent: only asks when
// isSubscribed is explicitly false.
export function ensureSubscribed(pub: TrackPub): void {
  if (pub.isSubscribed === false && pub.setSubscribed) {
    try {
      pub.setSubscribed(true);
    } catch {
      // best-effort — never let a subscribe request crash the surface
    }
  }
}

// The element each holder's track is currently attached to, so a forced re-attach of an
// UNCHANGED track onto a remounted element can release the previous one (LSC-650).
const attachedEls = new WeakMap<TrackHolder, HTMLMediaElement>();

// Attach one resolved track to its element, but SKIP the detach/re-attach when the
// resolved track is unchanged — so an UNRELATED publisher joining/leaving never flickers
// the on-program feed (ADR 0062). `force` re-attaches even an unchanged track (element
// remount / hold lift). `keepOnMiss` keeps the current track through a transient null while
// the slot is still assigned (LSC-425); a de-assigned slot or a forced re-attach detaches.
//
// An unchanged track moving to a NEW element (fullscreen toggle / layout remount) is first
// detached from its previous element (LSC-650). Without that the track stays attached to the
// stale element too, and livekit-client ≤2.22 only mutes the FIRST element it routes into the
// webAudioMix graph — every later element played the stream directly on top of the graph:
// the persistent fullscreen echo that only a rejoin cleared.
export function attachTrack(
  holder: TrackHolder,
  target: AttachableTrack | null,
  el: HTMLMediaElement | null,
  force: boolean,
  keepOnMiss = false,
): void {
  if (holder.current === target && !force) return;
  if (target === null && keepOnMiss && !force) return;
  const prevEl = attachedEls.get(holder);
  if (holder.current && holder.current !== target) {
    try {
      holder.current.detach();
    } catch {
      // never let teardown of a stale track crash the surface
    }
  } else if (holder.current && prevEl && prevEl !== el) {
    try {
      holder.current.detach(prevEl);
    } catch {
      // never let teardown of a stale element crash the surface
    }
  }
  holder.current = target;
  attachedEls.delete(holder);
  if (target && el) {
    try {
      target.attach(el); // livekit attach also (re)plays the element
      attachedEls.set(holder, el);
    } catch {
      // jsdom / detached element — the media is cosmetic under test
    }
  }
}

// Resolve one program source (matched by STABLE key, ADR 0042) to its live video + audio
// tracks and attach them, returning the live media kind (ADR 0043): a live video track →
// "video"; audio only → "audio"; nothing (set-but-absent = involuntary loss) → "none".
// Pass audioEl/audioHolder null to attach VIDEO ONLY (the talent monitor — talent hear each
// other via the talent-conversation plane, ADR 0218, so attaching program audio would echo).
export function attachProgramSlot(
  room: AttachRoom | null,
  sourceKey: string | undefined,
  videoEl: HTMLVideoElement | null,
  audioEl: HTMLMediaElement | null,
  videoHolder: TrackHolder,
  audioHolder: TrackHolder | null,
  force: boolean,
): SourceKind {
  // A publisher can briefly appear TWICE under a reconnect: the stale ghost + the fresh
  // rejoin share the same stable key (ADR 0042). Collect EVERY matching participant
  // (force-subscribing each track-less publication so the fresh one's media is requested
  // now — ADR 0075/0089), then pick the LIVE one via pickLiveCandidate (LSC-177).
  const matches: { hasVideo: boolean; hasAudio: boolean; video: AttachableTrack | null; audio: AttachableTrack | null }[] = [];
  if (sourceKey && room?.remoteParticipants) {
    for (const p of room.remoteParticipants.values()) {
      const part = p as {
        name?: string;
        identity?: string;
        videoTrackPublications?: Map<string, TrackPub>;
        audioTrackPublications?: Map<string, TrackPub>;
      };
      if (participantSourceKey(part) !== sourceKey) continue;
      let video: AttachableTrack | null = null;
      for (const pub of part.videoTrackPublications?.values() ?? []) {
        if (pub.videoTrack) {
          if (!video) video = pub.videoTrack;
        } else {
          ensureSubscribed(pub);
        }
      }
      let audio: AttachableTrack | null = null;
      for (const pub of part.audioTrackPublications?.values() ?? []) {
        if (pub.audioTrack) {
          if (!audio) audio = pub.audioTrack;
        } else {
          ensureSubscribed(pub);
        }
      }
      matches.push({ hasVideo: !!video, hasAudio: !!audio, video, audio });
      // no break — keep scanning other participants sharing this stable key
    }
  }
  const targetVideo = pickLiveCandidate(matches.filter((m) => m.hasVideo))?.video ?? null;
  const targetAudio = pickLiveCandidate(matches.filter((m) => m.hasAudio))?.audio ?? null;
  const keepOnMiss = !!sourceKey && !force;
  attachTrack(videoHolder, targetVideo, videoEl, force, keepOnMiss);
  if (audioHolder) attachTrack(audioHolder, targetAudio, audioEl, force, keepOnMiss);
  return targetVideo ? "video" : targetAudio ? "audio" : "none";
}
